// The shared clock for live operational screens — a kitchen ticket's age, a
// guest's wait, a call's hold time. One implementation used by the smart-page
// `timer` block, the `|age_min` / `|age_sec` token filters, `styleRules` that
// read an age, and the transaction lists' `elapsed` display format, so "how
// long has this been waiting" reads the same everywhere.
//
// Pure helpers only (no React): they run in the SSR prerenderer, in token
// substitution and in node tests. The ticking hook lives in `clock.ts`.

/**
 * Parse a date/time value the way a database hands it over: an ISO string, a
 * `YYYY-MM-DD HH:MM[:SS]` string (Safari refuses the space form), an epoch in
 * ms or s, or a Date. Returns epoch ms, or null when it is not a time.
 */
export function parseTime(value: unknown): number | null {
  if (value == null || value === '') return null
  if (value instanceof Date) {
    const t = value.getTime()
    return Number.isNaN(t) ? null : t
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null
    // Seconds-precision epochs are 10 digits; ms are 13.
    return value < 1e11 ? value * 1000 : value
  }
  let s = String(value).trim()
  if (!s) return null
  if (/^\d{10,13}$/.test(s)) return parseTime(Number(s))
  if (/^\d{4}-\d{2}-\d{2} \d{1,2}:\d{2}/.test(s)) s = s.replace(' ', 'T')
  const t = Date.parse(s)
  return Number.isNaN(t) ? null : t
}

/** Milliseconds from `from` to `until` (or `now`), never negative. Null when
 *  `from` is not a time. */
export function elapsedMs(from: unknown, now: number, until?: unknown): number | null {
  const start = parseTime(from)
  if (start == null) return null
  const stop = parseTime(until)
  const end = stop != null ? stop : now
  return Math.max(0, end - start)
}

export type ElapsedFormat = 'auto' | 'mm:ss' | 'h:mm'

const pad = (n: number) => String(n).padStart(2, '0')

/** `auto`: m:ss under an hour, then h:mm; `mm:ss` keeps counting minutes;
 *  `h:mm` drops the seconds. */
export function formatElapsed(ms: number, format: ElapsedFormat = 'auto'): string {
  const totalSec = Math.floor(Math.max(0, ms) / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (format === 'mm:ss') return `${pad(Math.floor(totalSec / 60))}:${pad(s)}`
  if (format === 'h:mm') return `${h}:${pad(m)}`
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

export type ElapsedTone = 'ok' | 'warn' | 'danger'

/** A number of minutes from a model value ('12', 12, '' → null). */
export function minutesOf(v: unknown): number | null {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(String(v).trim())
  return Number.isFinite(n) ? n : null
}

/** Which band `ms` falls in, given amber / red thresholds in MINUTES (either
 *  may be absent). Reaching a threshold counts as being in it. */
export function elapsedTone(ms: number, warnAfter: unknown, dangerAfter: unknown): ElapsedTone {
  const min = ms / 60000
  const danger = minutesOf(dangerAfter)
  const warn = minutesOf(warnAfter)
  if (danger != null && min >= danger) return 'danger'
  if (warn != null && min >= warn) return 'warn'
  return 'ok'
}

/** The tone's colours, from the page theme where it has them. */
export const TONE_COLOR: Record<ElapsedTone, string> = {
  ok: 'var(--pg-success, #15803d)',
  warn: 'var(--pg-warning, #b45309)',
  danger: 'var(--pg-danger, #b91c1c)',
}

/** Whole minutes / seconds since a time value ('' when it is not one). Used by
 *  the `{{col|age_min}}` token filters. */
export function ageOf(value: unknown, unit: 'min' | 'sec', now = Date.now()): string {
  const ms = elapsedMs(value, now)
  if (ms == null) return ''
  return String(Math.floor(ms / (unit === 'min' ? 60000 : 1000)))
}

/** True when a text / condition reads an age token, i.e. needs a live clock. */
export function readsAge(text: unknown): boolean {
  return typeof text === 'string' && /\|\s*age_(min|sec)\s*\}\}/.test(text)
}
